import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { Conversation } from '@assembly/slack'
import { useAccounts, type Account } from '../state/accounts'
import { useUi } from '../state/ui'
import { useActions } from '../data/actions'
import { useConversationName, useConversations, useDnd, useExternalOrg, usePresence, useTeam, useUser, useUsers, displayName } from '../data/hooks'
import { useWorkspace } from '../data/workspace'
import { usePlatform } from '../platform'
import { useSignIn } from '../auth'
import { useSidebar } from '../state/sidebar'
import { buildSidebar, deleteSection, isStarred, moveConversation, moveSection, sectionOf, setCollapsed, setDmWindow, setSort, star, unstar, DEFAULT_DM_DAYS, type SectionSort, type SidebarGroup } from '../sidebar/layout'
import { ConversationIcon } from './ChannelView'
import { Icon, Logo } from './Icon'
import { Emoji, IconButton, Menu, Spinner, UserAvatar, anchorFrom, useIsNarrow, useLongPress, type Anchor, type MenuItem } from './common'

/** Shown once a new UI build has been downloaded in the background (macOS). */
function UpdateBanner() {
  const platform = usePlatform()
  const [ready, setReady] = useState(false)
  const [applying, setApplying] = useState(false)
  useEffect(() => platform.updates?.onReady(() => setReady(true)), [platform])
  if (!ready) return null
  return (
    <button
      type="button"
      className="update-banner"
      disabled={applying}
      onClick={() => {
        setApplying(true)
        platform.updates?.apply().catch(() => setApplying(false))
      }}
    >
      {applying ? <Spinner size={14} /> : <Icon name="download" size={14} />}
      <span>Update ready</span>
      <strong>Reload</strong>
    </button>
  )
}

function TeamIcon({ account, size = 36 }: { account: Account; size?: number }) {
  return account.teamIcon ? (
    <img className="team-icon" src={account.teamIcon} alt={account.teamName} style={{ width: size, height: size }} />
  ) : (
    <span className="team-icon team-icon-fallback" style={{ width: size, height: size }}>
      {account.teamName.slice(0, 1).toUpperCase()}
    </span>
  )
}

export function WorkspaceRail() {
  const accounts = useAccounts((s) => s.accounts)
  const activeId = useAccounts((s) => s.activeId)
  const setActive = useAccounts((s) => s.setActive)
  const { start, busy } = useSignIn()
  const platform = usePlatform()
  return (
    <nav className="rail" aria-label="Workspaces">
      <div className="rail-logo" title="Assembly">
        <Logo size={26} />
      </div>
      {accounts.map((a, i) => (
        <button
          key={a.id}
          type="button"
          className={`rail-item ${a.id === activeId ? 'active' : ''}`}
          title={`${a.teamName}${i < 9 ? ` (⌘${i + 1})` : ''}`}
          onClick={() => setActive(a.id)}
        >
          <TeamIcon account={a} />
        </button>
      ))}
      {platform.canOAuth && (
        <button type="button" className="rail-item rail-add" title="Add a workspace" onClick={start} disabled={busy}>
          {busy ? <Spinner size={16} /> : <Icon name="plus" />}
        </button>
      )}
    </nav>
  )
}

// ------------------------------------------------------------------ drag and drop

type Drag = { kind: 'conv'; id: string; from: string; direct: boolean } | { kind: 'section'; id: string }
type DropHint = { section: string; index: number } | { beforeSection: string } | null

interface DndApi {
  drag: React.MutableRefObject<Drag | null>
  hint: DropHint
  setHint: (h: DropHint) => void
  drop: (target: { section: string; index?: number } | { beforeSection: string; after: boolean }) => void
}
const DndContext = createContext<DndApi | null>(null)

function useSidebarDnd(groups: SidebarGroup[]): DndApi {
  const edit = useSidebar((s) => s.edit)
  const drag = useRef<Drag | null>(null)
  const [hint, setHint] = useState<DropHint>(null)
  useEffect(() => {
    const clear = () => {
      drag.current = null
      setHint(null)
    }
    window.addEventListener('dragend', clear)
    window.addEventListener('drop', clear)
    return () => {
      window.removeEventListener('dragend', clear)
      window.removeEventListener('drop', clear)
    }
  }, [])
  return {
    drag,
    hint,
    setHint,
    drop: (target) => {
      const d = drag.current
      drag.current = null
      setHint(null)
      if (!d) return
      if (d.kind === 'section' && 'beforeSection' in target) {
        edit((l) => {
          const to = l.order.indexOf(target.beforeSection)
          return to === -1 || target.beforeSection === d.id ? l : moveSection(l, d.id, to + (target.after ? 1 : 0))
        })
      } else if (d.kind === 'conv' && 'section' in target) {
        const group = groups.find((g) => g.id === target.section)
        edit((l) => {
          // Positions only matter in manually sorted sections.
          if (!group || group.builtin || group.sort !== 'manual' || target.index === undefined) return moveConversation(l, d.id, target.section)
          // Translate the visible index into the section's stored item order.
          const items = l.sections.find((x) => x.id === target.section)?.items ?? []
          const before = group.conversations[target.index]?.id
          let index = before ? items.indexOf(before) : items.length
          const current = items.indexOf(d.id)
          if (current !== -1 && current < index) index--
          return moveConversation(l, d.id, target.section, index === -1 ? undefined : index)
        })
      }
    },
  }
}

// ------------------------------------------------------------------ conversation row

function SidebarItem({ conv, active, section, index }: { conv: Conversation; active: boolean; section: string; index: number }) {
  const name = useConversationName()
  const unread = useUi((s) => s.unread[conv.id] ?? 0)
  const mentions = useUi((s) => s.mentions[conv.id] ?? 0)
  const openChannel = useUi((s) => s.openChannel)
  const openPanel = useUi((s) => s.openPanel)
  const openModal = useUi((s) => s.openModal)
  const layout = useSidebar((s) => s.layout)
  const edit = useSidebar((s) => s.edit)
  const actions = useActions()
  const dnd = useContext(DndContext)
  const [menu, setMenu] = useState<Anchor | null>(null)
  const [moveMenu, setMoveMenu] = useState<Anchor | null>(null)
  const longPress = useLongPress(setMenu)
  const dmUser = useUser(conv.is_im ? conv.user : undefined)
  const org = useExternalOrg(dmUser)
  const starred = isStarred(layout, conv.id)
  const inSection = sectionOf(layout, conv.id)

  const items: (MenuItem | 'separator' | false)[] = [
    { label: 'Open', icon: 'chevronRight', onSelect: () => openChannel(conv.id) },
    { label: conv.is_im ? 'View profile' : 'Channel details', icon: 'info', onSelect: () => (conv.is_im && conv.user ? openPanel({ type: 'profile', user: conv.user }) : openPanel({ type: 'details', channel: conv.id })) },
    unread > 0 && { label: 'Mark as read', icon: 'check', onSelect: () => actions.markConversationRead(conv.id) },
    'separator',
    { label: starred ? 'Unstar' : 'Star', icon: 'star', onSelect: () => edit((l) => (starred ? unstar(l, conv.id) : star(l, conv.id))) },
    { label: 'Move to section…', icon: 'folder', onSelect: () => setMoveMenu(menu) },
    !!inSection && { label: 'Remove from section', icon: 'x', onSelect: () => edit((l) => moveConversation(l, conv.id, 'channels')) },
    'separator',
    conv.is_im || conv.is_mpim
      ? { label: 'Close conversation', icon: 'x', onSelect: () => actions.closeConversation(conv.id) }
      : !conv.is_general && { label: 'Leave channel', icon: 'door', danger: true, onSelect: () => actions.leaveChannel(conv.id) },
  ]

  const moveItems: (MenuItem | 'separator')[] = [
    ...layout.order
      .filter((id) => id !== (inSection ?? (conv.is_im || conv.is_mpim ? 'dms' : 'channels')))
      .filter((id) => !(id === 'channels' && (conv.is_im || conv.is_mpim)) && !(id === 'dms' && !conv.is_im && !conv.is_mpim))
      .map((id) => {
        const custom = layout.sections.find((x) => x.id === id)
        return { label: custom?.name ?? (id === 'dms' ? 'Direct messages' : 'Channels'), icon: (custom ? 'folder' : id === 'dms' ? 'users' : 'hash') as MenuItem['icon'], onSelect: () => edit((l) => moveConversation(l, conv.id, id)) }
      }),
    'separator',
    { label: 'New section…', icon: 'plus', onSelect: () => openModal({ type: 'section', conv: conv.id }) },
  ]

  const hint = dnd?.hint
  const dropBefore = !!hint && 'section' in hint && hint.section === section && hint.index === index

  return (
    <>
      <button
        type="button"
        className={`sidebar-item ${active ? 'active' : ''} ${unread ? 'unread' : ''} ${dropBefore ? 'drop-before' : ''}`}
        data-conv={conv.id}
        onClick={() => openChannel(conv.id)}
        draggable
        onDragStart={(e) => {
          if (!dnd) return
          dnd.drag.current = { kind: 'conv', id: conv.id, from: section, direct: !!(conv.is_im || conv.is_mpim) }
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', name(conv))
        }}
        onDragOver={(e) => {
          if (dnd?.drag.current?.kind !== 'conv') return
          e.preventDefault()
          e.stopPropagation()
          const rect = e.currentTarget.getBoundingClientRect()
          const at = e.clientY > rect.top + rect.height / 2 ? index + 1 : index
          if (!(hint && 'section' in hint && hint.section === section && hint.index === at)) dnd.setHint({ section, index: at })
        }}
        onDrop={(e) => {
          if (dnd?.drag.current?.kind !== 'conv') return
          e.preventDefault()
          e.stopPropagation()
          const rect = e.currentTarget.getBoundingClientRect()
          dnd.drop({ section, index: e.clientY > rect.top + rect.height / 2 ? index + 1 : index })
        }}
        {...longPress}
      >
        <span className="sidebar-item-icon">
          <ConversationIcon conv={conv} size={15} />
        </span>
        <span className="sidebar-item-name">
          {name(conv)}
          {org && <span className="sidebar-item-org">{org.name}</span>}
        </span>
        {dmUser?.profile?.status_emoji && <Emoji name={dmUser.profile.status_emoji.replace(/:/g, '')} />}
        {mentions > 0 && <span className="badge-count">{mentions}</span>}
      </button>
      {menu && <Menu anchor={menu} onClose={() => setMenu(null)} items={items} />}
      {moveMenu && <Menu anchor={moveMenu} onClose={() => setMoveMenu(null)} items={moveItems} />}
    </>
  )
}

// ------------------------------------------------------------------ section

const SORT_LABELS: Record<SectionSort, string> = { alpha: 'Alphabetical', recent: 'Recent activity', manual: 'Custom (drag to order)' }

function SectionView({ group, position, total, extra, onAdd }: { group: SidebarGroup; position: number; total: number; extra?: React.ReactNode; onAdd?: (anchor: Anchor) => void }) {
  const edit = useSidebar((s) => s.edit)
  const openModal = useUi((s) => s.openModal)
  const channelId = useUi((s) => s.channelId)
  const view = useUi((s) => s.view)
  const unread = useUi((s) => s.unread)
  const actions = useActions()
  const dnd = useContext(DndContext)
  const [menu, setMenu] = useState<Anchor | null>(null)
  const hint = dnd?.hint
  const isDropTarget = !!hint && 'section' in hint && hint.section === group.id
  const dropAtEnd = isDropTarget && (hint as { index: number }).index >= group.conversations.length
  const sectionBefore = !!hint && 'beforeSection' in hint && hint.beforeSection === group.id
  const unreadHere = group.conversations.filter((c) => unread[c.id])
  const dmDays = useSidebar((s) => s.layout.builtins.dms.days)

  const items: (MenuItem | 'separator' | false)[] = [
    !group.builtin && { label: 'Edit section', icon: 'pencil', onSelect: () => openModal({ type: 'section', id: group.id }) },
    group.id === 'channels' && { label: 'Organize with AI…', icon: 'sparkles', onSelect: () => openModal({ type: 'aiOrganize' }) },
    group.id === 'channels' && 'separator',
    ...(['alpha', 'recent', 'manual'] as SectionSort[])
      .filter((sort) => !(group.builtin && sort === 'manual'))
      .map((sort) => ({ label: `Sort: ${SORT_LABELS[sort]}`, icon: (group.sort === sort ? 'check' : 'sort') as MenuItem['icon'], onSelect: () => edit((l) => setSort(l, group.id, sort)) })),
    ...(group.id === 'dms'
      ? (
          [
            [1, 'Show: Last day'],
            [7, 'Show: Last 7 days'],
            [30, 'Show: Last 30 days'],
            [0, 'Show: All conversations'],
          ] as const
        ).map(([days, label]) => ({
          label,
          icon: ((dmDays ?? DEFAULT_DM_DAYS) === days ? 'check' : 'clock') as MenuItem['icon'],
          onSelect: () => edit((l) => setDmWindow(l, days)),
        }))
      : []),
    unreadHere.length > 0 && { label: 'Mark all as read', icon: 'check', onSelect: () => unreadHere.forEach((c) => actions.markConversationRead(c.id)) },
    'separator',
    position > 0 && { label: 'Move section up', icon: 'arrowUp', onSelect: () => edit((l) => moveSection(l, group.id, position - 1)) },
    position < total - 1 && { label: 'Move section down', icon: 'chevronDown', onSelect: () => edit((l) => moveSection(l, group.id, position + 2)) },
    !group.builtin && 'separator',
    !group.builtin && {
      label: 'Delete section',
      icon: 'trash',
      danger: true,
      onSelect: () =>
        openModal({
          type: 'confirm',
          title: `Delete “${group.name}”?`,
          body: 'Its conversations go back to Channels and Direct messages. Nothing is deleted in Slack.',
          confirm: 'Delete section',
          danger: true,
          onConfirm: () => edit((l) => deleteSection(l, group.id)),
        }),
    },
  ]

  const accepts = (e: React.DragEvent) => {
    const d = dnd?.drag.current
    if (!d || d.kind !== 'conv') return false
    // Built-in sections only take back what belongs to them (channels vs. DMs).
    if ((group.id === 'channels' && d.direct) || (group.id === 'dms' && !d.direct)) return false
    e.preventDefault()
    return true
  }

  return (
    <section
      className={`sidebar-section ${isDropTarget ? 'drop-target' : ''} ${sectionBefore ? 'section-drop-before' : ''}`}
      data-section={group.id}
      aria-label={group.name}
      onDragOver={(e) => {
        if (!accepts(e)) return
        if (!isDropTarget || !dropAtEnd) dnd!.setHint({ section: group.id, index: group.conversations.length })
      }}
      onDrop={(e) => {
        if (!accepts(e)) return
        dnd!.drop({ section: group.id })
      }}
    >
      <div
        className="sidebar-section-header"
        draggable
        onDragStart={(e) => {
          if (!dnd) return
          dnd.drag.current = { kind: 'section', id: group.id }
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', group.name)
        }}
        onDragOver={(e) => {
          if (dnd?.drag.current?.kind !== 'section') return
          e.preventDefault()
          e.stopPropagation()
          if (!sectionBefore) dnd.setHint({ beforeSection: group.id })
        }}
        onDrop={(e) => {
          if (dnd?.drag.current?.kind !== 'section') return
          e.preventDefault()
          e.stopPropagation()
          const rect = e.currentTarget.getBoundingClientRect()
          dnd.drop({ beforeSection: group.id, after: e.clientY > rect.top + rect.height / 2 })
        }}
      >
        <button type="button" className="section-toggle" aria-expanded={!group.collapsed} onClick={() => edit((l) => setCollapsed(l, group.id, !group.collapsed))}>
          <Icon name={group.collapsed ? 'chevronRight' : 'chevronDown'} size={14} />
          {group.emoji && <Emoji name={group.emoji} size={14} />}
          <span className="section-name">{group.name}</span>
        </button>
        {group.collapsed && group.unreadCount > 0 && <span className="section-unread" aria-label={`${group.unreadCount} unread`}>{group.unreadCount}</span>}
        <span className="section-actions">
          <IconButton icon="more" label={`${group.name} options`} size={15} onClick={(e) => setMenu(anchorFrom(e))} />
          {onAdd && <IconButton icon="plus" label={group.id === 'dms' ? 'New message' : 'Add channels'} size={15} onClick={(e) => onAdd(anchorFrom(e))} />}
        </span>
      </div>
      <div className="sidebar-section-body">
        {group.conversations.map((c, i) => (
          <SidebarItem key={c.id} conv={c} section={group.id} index={i} active={view.type === 'channel' && channelId === c.id} />
        ))}
        {dropAtEnd && <div className="drop-line" />}
        {!group.builtin && !group.collapsed && group.conversations.length === 0 && <div className="section-empty">Drag conversations here</div>}
        {!group.collapsed && extra}
      </div>
      {menu && <Menu anchor={menu} onClose={() => setMenu(null)} items={items} />}
    </section>
  )
}

function UserMenuButton() {
  const { account } = useWorkspace()
  const me = useUser(account.userId)
  const realtime = useUi((s) => s.realtime)
  const { data: presence } = usePresence(account.userId)
  const { data: dnd } = useDnd()
  const actions = useActions()
  const openModal = useUi((s) => s.openModal)
  const openPanel = useUi((s) => s.openPanel)
  const removeAccount = useAccounts((s) => s.remove)
  const [menu, setMenu] = useState<Anchor | null>(null)
  const snoozed = dnd?.snooze_enabled

  const items: (MenuItem | 'separator' | false)[] = [
    { label: 'Profile', icon: 'user', onSelect: () => openPanel({ type: 'profile', user: account.userId }) },
    { label: 'Edit profile', icon: 'pencil', onSelect: () => openModal({ type: 'editProfile' }) },
    { label: me?.profile?.status_text || me?.profile?.status_emoji ? 'Update status' : 'Set a status', icon: 'smile', onSelect: () => openModal({ type: 'setStatus' }) },
    { label: presence === 'away' ? 'Set yourself as active' : 'Set yourself as away', icon: 'dot', onSelect: () => actions.setPresence(presence === 'away' ? 'auto' : 'away') },
    'separator',
    snoozed
      ? { label: 'Resume notifications', icon: 'bell', onSelect: () => actions.endSnooze() }
      : { label: 'Pause notifications for 1 hour', icon: 'bellOff', onSelect: () => actions.snooze(60) },
    !snoozed && { label: 'Pause until tomorrow', icon: 'moon', onSelect: () => {
      const t = new Date()
      t.setDate(t.getDate() + 1)
      t.setHours(9, 0, 0, 0)
      actions.snooze(Math.round((t.getTime() - Date.now()) / 60_000))
    } },
    'separator',
    { label: `Sign out of ${account.teamName}`, icon: 'logout', danger: true, onSelect: () => openModal({ type: 'confirm', title: 'Sign out?', body: `You’ll be signed out of ${account.teamName} on this device.`, confirm: 'Sign out', onConfirm: () => removeAccount(account.id) }) },
  ]

  return (
    <>
      <button type="button" className="user-button" onClick={(e) => setMenu(anchorFrom(e))}>
        <span className="user-button-avatar">
          <UserAvatar id={account.userId} size={28} />
          <span className={`presence ${presence === 'active' ? 'active' : ''} ${snoozed ? 'dnd' : ''}`} />
        </span>
        <span className="user-button-text">
          <strong>{displayName(me, account.userName ?? '')}</strong>
          <span className="muted">
            {me?.profile?.status_emoji && <Emoji name={me.profile.status_emoji.replace(/:/g, '')} />} {me?.profile?.status_text || (snoozed ? 'Notifications paused' : presence === 'away' ? 'Away' : 'Active')}
          </span>
        </span>
        <span className={`realtime-dot ${realtime}`} title={realtime === 'live' ? 'Connected (real time)' : realtime === 'polling' ? 'Updating periodically' : 'Connecting…'} />
      </button>
      {menu && <Menu anchor={menu} placement="above" onClose={() => setMenu(null)} items={items} />}
    </>
  )
}

export function Sidebar() {
  const { account } = useWorkspace()
  const { data: team } = useTeam()
  const { data: convs, isLoading } = useConversations()
  const { data: users } = useUsers()
  const channelId = useUi((s) => s.channelId)
  const view = useUi((s) => s.view)
  const openModal = useUi((s) => s.openModal)
  const setView = useUi((s) => s.setView)
  const unread = useUi((s) => s.unread)
  const recent = useUi((s) => s.recent)
  const latest = useUi((s) => s.latest)
  const touched = useUi((s) => s.touched)
  const lastMessage = useUi((s) => s.lastMessage)
  const name = useConversationName()
  const narrow = useIsNarrow()
  const accounts = useAccounts((s) => s.accounts)
  const setActive = useAccounts((s) => s.setActive)
  const { start: addWorkspace } = useSignIn()
  const platform = usePlatform()
  const [teamMenu, setTeamMenu] = useState<Anchor | null>(null)
  const [channelMenu, setChannelMenu] = useState<Anchor | null>(null)
  const [unreadsOnly, setUnreadsOnly] = useState(false)

  const layout = useSidebar((s) => s.layout)
  const groups = useMemo(() => {
    const list = (convs ?? []).filter((c) => !c.is_archived && !(c.is_im && (c.is_user_deleted || users?.get(c.user ?? '')?.deleted)))
    return buildSidebar(layout, list, {
      name,
      // Last activity in ms: Slack's `updated`, the newest message the unread scan saw,
      // and live activity (opened, or a message arrived while Assembly was running).
      activity: (c) => lastMessage[c.id] ?? Math.max(c.updated ?? 0, latest[c.id] ? Number(latest[c.id]) * 1000 : 0, recent[c.id] ?? 0, touched[c.id] ?? 0),
      lastMessageAt: (c) => lastMessage[c.id],
      isUnread: (c) => !!unread[c.id],
      current: view.type === 'channel' ? channelId : null,
      unreadsOnly,
    })
  }, [convs, users, layout, name, unread, recent, latest, touched, lastMessage, unreadsOnly, channelId, view.type])
  const dnd = useSidebarDnd(groups)

  return (
    <aside className="sidebar">
      <header className="sidebar-header">
        <button type="button" className="team-name" onClick={(e) => setTeamMenu(anchorFrom(e))}>
          <span>{team?.name ?? account.teamName}</span>
          <Icon name="chevronDown" size={14} />
        </button>
        <IconButton icon="compose" label="New message" onClick={() => openModal({ type: 'newMessage' })} />
      </header>

      <div className="sidebar-scroll">
        <nav className="sidebar-nav">
          <button type="button" className="sidebar-jump" onClick={() => openModal({ type: 'switcher' })}>
            <Icon name="search" size={14} />
            <span>Jump to…</span>
            {!narrow && <kbd>⌘K</kbd>}
          </button>
          <button type="button" className={`sidebar-item ${view.type === 'search' ? 'active' : ''}`} onClick={() => setView({ type: 'search', query: '' })}>
            <span className="sidebar-item-icon">
              <Icon name="search" size={15} />
            </span>
            <span className="sidebar-item-name">Search messages</span>
            {!narrow && <kbd>⌘G</kbd>}
          </button>
          <button type="button" className="sidebar-item" onClick={() => openModal({ type: 'files' })}>
            <span className="sidebar-item-icon">
              <Icon name="file" size={15} />
            </span>
            <span className="sidebar-item-name">Files</span>
          </button>
          <button type="button" className="sidebar-item" onClick={() => openModal({ type: 'reminders' })}>
            <span className="sidebar-item-icon">
              <Icon name="bookmark" size={15} />
            </span>
            <span className="sidebar-item-name">Reminders</span>
          </button>
          <button type="button" className="sidebar-item" onClick={() => openModal({ type: 'scheduled' })}>
            <span className="sidebar-item-icon">
              <Icon name="clock" size={15} />
            </span>
            <span className="sidebar-item-name">Scheduled</span>
          </button>
          <button type="button" className={`sidebar-item ${unreadsOnly ? 'active-soft' : ''}`} onClick={() => setUnreadsOnly(!unreadsOnly)}>
            <span className="sidebar-item-icon">
              <Icon name="eye" size={15} />
            </span>
            <span className="sidebar-item-name">{unreadsOnly ? 'Show all' : 'Unreads only'}</span>
          </button>
        </nav>

        {isLoading ? (
          <div className="loading-row">
            <Spinner />
          </div>
        ) : (
          <DndContext.Provider value={dnd}>
            {groups.map((g, i) => (
              <SectionView
                key={g.id}
                group={g}
                position={i}
                total={groups.length}
                onAdd={g.id === 'channels' ? setChannelMenu : g.id === 'dms' ? () => openModal({ type: 'newMessage' }) : undefined}
                extra={
                  g.id === 'channels' ? (
                    <button type="button" className="sidebar-item subtle" onClick={() => openModal({ type: 'browseChannels' })}>
                      <span className="sidebar-item-icon">
                        <Icon name="plus" size={15} />
                      </span>
                      <span className="sidebar-item-name">Add channels</span>
                    </button>
                  ) : undefined
                }
              />
            ))}
            <button type="button" className="sidebar-item subtle add-section" onClick={() => openModal({ type: 'section' })}>
              <span className="sidebar-item-icon">
                <Icon name="folder" size={15} />
              </span>
              <span className="sidebar-item-name">Add a section</span>
            </button>
          </DndContext.Provider>
        )}
      </div>

      <footer className="sidebar-footer">
        <UpdateBanner />
        <UserMenuButton />
      </footer>

      {teamMenu && (
        <Menu
          anchor={teamMenu}
          onClose={() => setTeamMenu(null)}
          items={[
            ...(narrow || accounts.length > 1
              ? accounts.map((a) => ({ label: a.teamName, icon: a.id === account.id ? ('check' as const) : undefined, onSelect: () => setActive(a.id) }))
              : []),
            platform.canOAuth && { label: 'Add a workspace', icon: 'plus', onSelect: addWorkspace },
            'separator',
            { label: 'Create a channel', icon: 'hash', onSelect: () => openModal({ type: 'createChannel' }) },
            { label: 'Create a section', icon: 'folder', onSelect: () => openModal({ type: 'section' }) },
            { label: 'Organize channels with AI…', icon: 'sparkles', onSelect: () => openModal({ type: 'aiOrganize' }) },
            { label: 'Browse channels', icon: 'search', onSelect: () => openModal({ type: 'browseChannels' }) },
            { label: 'Keyboard shortcuts', icon: 'sliders', onSelect: () => openModal({ type: 'shortcuts' }) },
            team?.domain && { label: 'Open in Slack', icon: 'external', onSelect: () => platform.openExternal(`https://${team.domain}.slack.com`) },
          ]}
        />
      )}
      {channelMenu && (
        <Menu
          anchor={channelMenu}
          onClose={() => setChannelMenu(null)}
          items={[
            { label: 'Create a new channel', icon: 'plus', onSelect: () => openModal({ type: 'createChannel' }) },
            { label: 'Browse channels', icon: 'search', onSelect: () => openModal({ type: 'browseChannels' }) },
            'separator',
            { label: 'Create a section', icon: 'folder', onSelect: () => openModal({ type: 'section' }) },
          ]}
        />
      )}
    </aside>
  )
}
